/**
 * Credit grant history read contract (router#2543).
 *
 * Lists every grant, active and ended, for the caller's visible book.
 * Column 7 ("used") is live debt `max(0, −balance)` measured at `now`
 * for an active grant and at `ended_at` for an ended grant (platform-api
 * reconstructs the latter). Days-to-topup is a UTC+8 whole-day count,
 * and only for a temporary grant that ended as `repaid` — never 0 as a
 * stand-in for other reasons.
 *
 * Summary totals are over the full filtered visible scope, never the page.
 * Amounts travel as the operator-entered CNY plus the frozen rate; this
 * module does not reconvert. Money is integer micro-USD.
 *
 * For each grant row:
 * - `repaid_usd_micros` = Σ inbound ledger rows of the grant's account with `created_at >= starts_at AND created_at <= COALESCE(ended_at, now)` (money that arrived while the line was open, including the inbound that ended a `repaid` grant) PLUS Σ `billing_statement_settlements.amount_usd_micros` of the grant's one-shot statement, if one exists (payments made against the bill an expired bridge was converted into).
 * - `outstanding_usd_micros` (number | null) = active → live debt now (same number as `used_usd_micros`); ended `repaid` → 0; ended with a one-shot statement → statement `status = 'closed'` ? 0 : `max(0, closing_ar_usd_micros − Σ its settlements)`; ended `superseded` → null (the debt carried into the replacing grant); ended `manual`/`expired` WITHOUT a one-shot statement → `used_usd_micros` (the debt at the instant it ended, still on the account).
 * - `repayment_status`: `'settled'` when outstanding === 0; `'carried_over'` when outstanding === null and end_reason === 'superseded'; `'unpaid'` when outstanding > 0 and repaid === 0; `'partial'` when outstanding > 0 and repaid > 0.
 * - `one_shot_statement`: `{ id, status, closing_ar_usd_micros, settled_usd_micros } | null`.
 */

import {
  type CreditGrant,
  type CreditGrantEndReason,
  type CreditGrantType,
} from './credit-grants.ts';
import { calendarDaysBetween } from './credit-overdue.ts';
import { isCalendarDateYmd, type CreditOpsEvent } from './temporary-credit.ts';

export const CREDIT_GRANT_HISTORY_DEFAULT_LIMIT = 50;
export const CREDIT_GRANT_HISTORY_MAX_LIMIT = 200;
export const CREDIT_GRANT_HISTORY_QUERY_MAX_CHARS = 120;

export const CREDIT_GRANT_HISTORY_STATUS_FILTERS = ['all', 'active', 'ended'] as const;
export type CreditGrantHistoryStatusFilter = (typeof CREDIT_GRANT_HISTORY_STATUS_FILTERS)[number];

export const CREDIT_GRANT_REPAYMENT_STATUSES = [
  'settled',
  'partial',
  'unpaid',
  'carried_over',
] as const;
export type CreditGrantRepaymentStatus = (typeof CREDIT_GRANT_REPAYMENT_STATUSES)[number];

export const CREDIT_GRANT_HISTORY_QUERY_KEYS = [
  'customer',
  'manager',
  'grant_type',
  'status',
  'end_reason',
  'starts_from',
  'starts_to',
  'limit',
  'offset',
] as const;

export function isCreditGrantHistoryStatusFilter(v: string): v is CreditGrantHistoryStatusFilter {
  for (const candidate of CREDIT_GRANT_HISTORY_STATUS_FILTERS) {
    if (candidate === v) return true;
  }
  return false;
}

export function isCreditGrantRepaymentStatus(v: string): v is CreditGrantRepaymentStatus {
  for (const candidate of CREDIT_GRANT_REPAYMENT_STATUSES) {
    if (candidate === v) return true;
  }
  return false;
}

/** Where column 7 ("used") was measured. */
export type CreditGrantUsedMeasuredAt = 'now' | 'ended_at';

export interface CreditGrantHistoryCustomerManager {
  readonly ops_user_id: string;
  readonly email: string;
  readonly display_name: string; // same COALESCE as internal-customer-managers.ts
}

export interface CreditGrantHistoryRow {
  readonly grant: CreditGrant;
  readonly org_id: string | null;
  readonly org_name: string | null;
  /** CURRENT assignment (owner decision 2026-09-20). Null = unassigned. */
  readonly customer_manager: CreditGrantHistoryCustomerManager | null;
  /** Debt attributable to the grant: max(0, −balance) at `used_measured_at`. */
  readonly used_usd_micros: number;
  readonly used_measured_at: CreditGrantUsedMeasuredAt;
  /**
   * Whole UTC+8 calendar days starts_at → ended_at, ONLY for
   * grant_type='temporary' AND end_reason='repaid'; null otherwise
   * (never 0 as a stand-in).
   */
  readonly days_to_topup: number | null;
  readonly repaid_usd_micros: number;
  readonly outstanding_usd_micros: number | null;
  readonly repayment_status: CreditGrantRepaymentStatus;
  readonly one_shot_statement: CreditGrantOneShotStatementView | null;
  /** credit_ops_events WHERE grant_id = grant.id, created_at ASC */
  readonly ops_events: readonly CreditOpsEvent[];
}

export interface CreditGrantOneShotStatementView {
  readonly id: string;
  readonly status: string;
  readonly closing_ar_usd_micros: number;
  readonly settled_usd_micros: number;
}

export interface CreditGrantHistorySummary {
  /** All four are over the FULL filtered visible scope, never the page. */
  readonly total: number;
  readonly active_count: number;
  readonly ended_count: number;
  readonly end_reason_counts: Readonly<Record<CreditGrantEndReason, number>>;
  /** temporary+repaid rows only; null when there are none. Mean of days_to_topup, rounded to 1 decimal. */
  readonly average_days_to_topup: number | null;
}

export interface GetCreditGrantHistoryResponse {
  readonly grants: readonly CreditGrantHistoryRow[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
  readonly summary: CreditGrantHistorySummary;
  readonly generated_at: string;
}

export function creditGrantHistoryEndReasonCountsZero(): Record<CreditGrantEndReason, number> {
  return {
    expired: 0,
    repaid: 0,
    manual: 0,
    superseded: 0,
  };
}

export function emptyCreditGrantHistoryResponse(generatedAt: string): GetCreditGrantHistoryResponse {
  return {
    grants: [],
    total: 0,
    limit: CREDIT_GRANT_HISTORY_DEFAULT_LIMIT,
    offset: 0,
    summary: {
      total: 0,
      active_count: 0,
      ended_count: 0,
      end_reason_counts: creditGrantHistoryEndReasonCountsZero(),
      average_days_to_topup: null,
    },
    generated_at: generatedAt,
  };
}

function requireCalendarDate(value: string, field: string): string {
  if (!isCalendarDateYmd(value)) {
    throw new Error(`${field} must be a UTC+8 calendar date YYYY-MM-DD`);
  }
  return value;
}

/**
 * Pure classifier for a grant row's repayment_status. Throws on negative
 * repaid/outstanding inputs, and when outstanding is null with a
 * non-superseded end reason (programming error).
 */
export function classifyCreditGrantRepayment(input: {
  repaidUsdMicros: number;
  outstandingUsdMicros: number | null;
  endReason: CreditGrantEndReason | null;
}): CreditGrantRepaymentStatus {
  if (!Number.isSafeInteger(input.repaidUsdMicros) || input.repaidUsdMicros < 0) {
    throw new Error('repaidUsdMicros must be a non-negative safe integer');
  }
  if (input.outstandingUsdMicros === null) {
    if (input.endReason !== 'superseded') {
      throw new Error('outstandingUsdMicros is null only when endReason is superseded');
    }
    return 'carried_over';
  }
  if (!Number.isSafeInteger(input.outstandingUsdMicros) || input.outstandingUsdMicros < 0) {
    throw new Error('outstandingUsdMicros must be a non-negative safe integer or null');
  }
  if (input.outstandingUsdMicros === 0) return 'settled';
  if (input.repaidUsdMicros === 0) return 'unpaid';
  return 'partial';
}

/**
 * Pure: null unless temporary && repaid; else calendarDaysBetween(startsOn, endedOn).
 * Throws if repaid but ended_at null.
 */
export function creditGrantDaysToTopup(input: {
  grantType: CreditGrantType;
  endReason: CreditGrantEndReason | null;
  startsOn: string;
  endedOn: string | null;
}): number | null {
  if (input.grantType !== 'temporary' || input.endReason !== 'repaid') {
    return null;
  }
  if (input.endedOn === null) {
    throw new Error('endedOn is required when endReason is repaid');
  }
  const startsOn = requireCalendarDate(input.startsOn, 'startsOn');
  const endedOn = requireCalendarDate(input.endedOn, 'endedOn');
  return calendarDaysBetween(startsOn, endedOn);
}
